import {
    Badge,
    Box,
    Button,
    Grid,
    Header,
    Multiselect,
    Pagination,
    SpaceBetween,
    Table,
    TableProps,
} from '@amzn/awsui-components-react';
import React, {useState} from 'react';
import {useCollection} from '@amzn/awsui-collection-hooks';
import {BridgeType, InboundBridgeRecord, NodeFilterData} from './index';

const tableColumnDefinitions = (): TableProps.ColumnDefinition<any>[] => {
    return [
        {
            id: 'date',
            header: 'Date',
            cell: (item: InboundBridgeTableRecord) => formatCellText(item.bucketType, item.date),
            isRowHeader: true,
        },
        {
            id: 'node',
            header: 'Node',
            cell: (item: InboundBridgeTableRecord) => formatCellText(item.bucketType, item.node),
            isRowHeader: true,
        },
        {
            id: 'channel',
            header: 'Channel',
            cell: (item: InboundBridgeTableRecord) => formatCellText(item.bucketType, item.granularity?.['channel']),
            isRowHeader: true,
        },
        {
            id: 'sourceLocation',
            header: 'Source',
            cell: (item: InboundBridgeTableRecord) => formatCellText(item.bucketType, item.granularity?.['sourceLocation']),
            isRowHeader: true,
        },
        {
            id: 'ibType',
            header: 'Inbound Type',
            cell: (item: InboundBridgeTableRecord) => formatCellText(item.bucketType, item.granularity?.['ibType']),
            isRowHeader: true,
        },
        {
            id: 'tempZone',
            header: 'Temp Zone',
            cell: (item: InboundBridgeTableRecord) => formatCellText(item.bucketType, item.granularity?.['tempZone']),
            isRowHeader: true,
        },
        {
            id: 'bucketSeqId',
            header: 'Bucket Seq',
            cell: (item: InboundBridgeTableRecord) => formatCellText(item.bucketType, item.bucketSeqId),
            isRowHeader: true,
        },
        {
            id: 'bucketName',
            header: 'Bucket',
            cell: (item: InboundBridgeTableRecord) => {
                if (item.bucketType === 'plan') {
                    return (
                        <SpaceBetween direction="horizontal" size="xxs">
                            <strong>{item.bucketName}</strong>
                            <Badge color="green">Plan</Badge>
                        </SpaceBetween>
                    );
                } else if (item.bucketType === 'actuals') {
                    return (
                        <SpaceBetween direction="horizontal" size="xxs">
                            <strong>{item.bucketName}</strong>
                            <Badge color="blue">Actuals</Badge>
                        </SpaceBetween>
                    );
                } else {
                    return item.bucketName;
                }
            },
            isRowHeader: true,
        },
        {
            id: 'bucketValue',
            header: 'Value',
            cell: (item: InboundBridgeTableRecord) =>
                formatCellText(
                    item.bucketType,
                    typeof item.bucketValue === 'number'
                        ? item.bucketValue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                        : item.bucketValue
                ),
            isRowHeader: true,
        },
        {
            id: 'bucketError',
            header: 'Error',
            cell: (item: InboundBridgeTableRecord) =>
                formatCellText(
                    item.bucketType,
                    typeof item.bucketError === 'number'
                        ? item.bucketError.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                        : item.bucketError
                ),
            isRowHeader: true,
        },
        {
            id: 'bucketWape',
            header: 'APE',
            cell: (item: InboundBridgeTableRecord) => formatCellText(item.bucketType, item.bucketWape),
            isRowHeader: true,
        },
    ];
};

const TABLE_PAGE_SIZE = 50;
const COMPONENT_TEXTS = {
    table_title: 'Bridge Data Details',
    table_description: 'The table contains top-five worst-performing source locations/vendors for each date and granularity',
    download_csv: 'Download CSV',
    balance_dates_filter_placeholder: 'Balance Dates',
    source_locations_filter_placeholder: 'Source Locations',
} as const;

export function InboundBridgeTable({
    bridgeType,
    tenant,
    bridgeData,
    selectedFilter,
    isLoading,
}: {
    bridgeType: BridgeType;
    tenant: {
        business: string;
        country: string;
    };
    bridgeData: InboundBridgeRecord[];
    selectedFilter: NodeFilterData | undefined;
    isLoading: boolean;
}) {
    const [tableSelectedDates, setTableSelectedDates] = useState<string[]>([]);
    const [tableSelectedSources, setTableSelectedSources] = useState<string[]>([]);

    const distinctSourceLocations = [...new Set(bridgeData.map((item) => item.granularity?.['sourceLocation']).filter(Boolean))];

    const tableEmpty = (
        <Box textAlign="center" color="inherit">
            <b>No matching data</b>
        </Box>
    );

    const matchesFilter = (filterArray: string[] | undefined, value: string | undefined) =>
        !filterArray || filterArray.length === 0 || filterArray.includes(value || '');

    const bridgeTableData: InboundBridgeTableRecord[] = processRecords(bridgeType, bridgeData);

    const {items, collectionProps, paginationProps} = useCollection(bridgeTableData, {
        filtering: {
            empty: tableEmpty,
            noMatch: tableEmpty,
            filteringFunction(item) {
                return (
                    (tableSelectedDates.length === 0 || tableSelectedDates.includes(item.date)) &&
                    (tableSelectedSources.length === 0 || tableSelectedSources.includes(item.granularity?.['sourceLocation'])) &&
                    matchesFilter(selectedFilter?.channels, item.granularity?.['channel']) &&
                    matchesFilter(selectedFilter?.ibTypes, item.granularity?.['ibType']) &&
                    matchesFilter(selectedFilter?.tempZones, item.granularity?.['tempZone'])
                );
            },
        },
        pagination: {pageSize: TABLE_PAGE_SIZE},
        sorting: {
            defaultState: {
                sortingColumn: {sortingField: 'date'},
                isDescending: false,
            },
        },
        selection: {
            keepSelection: true,
        },
    });

    return (
        <Table
            data-testid="page-table"
            {...collectionProps}
            stickyHeader
            stripedRows
            header={
                <Header
                    variant="h2"
                    description={COMPONENT_TEXTS.table_description}
                    actions={<Button onClick={downloadCsv}>{COMPONENT_TEXTS.download_csv}</Button>}
                >
                    {COMPONENT_TEXTS.table_title}
                </Header>
            }
            items={items}
            pagination={<Pagination {...paginationProps} />}
            filter={
                <React.Fragment>
                    <Grid gridDefinition={[{colspan: 4}, {colspan: 2}]}>
                        <Multiselect
                            data-testid="table-date-multiselect"
                            selectedOptions={tableSelectedDates.map((item) => ({label: item, value: item}))}
                            onChange={async ({detail}) => {
                                const dates = detail.selectedOptions.map((item) => item.value!);
                                setTableSelectedDates(dates);
                            }}
                            options={getSelectedDateList().map((item) => ({label: item, value: item}))}
                            tokenLimit={5}
                            filteringType="auto"
                            placeholder={COMPONENT_TEXTS.balance_dates_filter_placeholder}
                        />
                        <Multiselect
                            data-testid="table-source-multiselect"
                            selectedOptions={tableSelectedSources.map((item) => ({label: item, value: item}))}
                            onChange={async ({detail}) => {
                                const sources = detail.selectedOptions.map((item) => item.value!);
                                setTableSelectedSources(sources);
                            }}
                            options={distinctSourceLocations.map((item) => ({label: item, value: item}))}
                            tokenLimit={5}
                            filteringType="auto"
                            placeholder={COMPONENT_TEXTS.source_locations_filter_placeholder}
                        />
                    </Grid>
                </React.Fragment>
            }
            columnDefinitions={tableColumnDefinitions()}
            loading={isLoading}
        />
    );

    function getSelectedDateList(): string[] {
        const startDate = selectedFilter?.balanceDateStart ?? '';
        const endDate = selectedFilter?.balanceDateEnd ?? '';
        const dates: string[] = [];
        const current = new Date(startDate);
        const end = new Date(endDate);

        while (current <= end) {
            dates.push(current.toISOString().split('T')[0]);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    }

    function downloadCsv(): void {
        const convertToCSV = (data: InboundBridgeTableRecord[]): string => {
            const csvHeader = [
                'date',
                'node',
                'channel',
                'source_location',
                'ib_type',
                'temp_zone',
                'bucket_seq_id',
                'bucket_name',
                'bucket_type',
                'bucket_value',
                'bucket_error',
                'bucket_wape',
            ].join(',');

            const csvRows = data.map((row: InboundBridgeTableRecord) =>
                [
                    row.date.toString(),
                    row.node,
                    row.granularity?.['channel'] ?? '',
                    row.granularity?.['sourceLocation'] ?? '',
                    row.granularity?.['ibType'] ?? '',
                    row.granularity?.['tempZone'] ?? '',
                    row.bucketSeqId.toString(),
                    '"' + row.bucketName + '"',
                    row.bucketType,
                    row.bucketValue.toString(),
                    row.bucketError.toString(),
                    row.bucketWape.toString(),
                ].join(',')
            );

            return [csvHeader, ...csvRows].join('\n');
        };

        const csvContent = convertToCSV(bridgeTableData);
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = Object.assign(document.createElement('a'), {
            href: url,
            download: `${tenant.business}_${tenant.country}_${bridgeType}_bridge_data.csv`,
        });
        link.click();
        URL.revokeObjectURL(url);
    }
}

function processRecords(bridgeType: BridgeType, records: InboundBridgeRecord[]): InboundBridgeTableRecord[] {
    if (bridgeType === BridgeType.P2A) {
        const groups = new Map<string, InboundBridgeRecord[]>();

        records.forEach((record) => {
            const key = `${record.date}-${record.node}-${JSON.stringify(record.granularity)}`;
            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key)!.push(record);
        });

        const result: InboundBridgeTableRecord[] = [];

        groups.forEach((groupRecords) => {
            const sorted = [...groupRecords].sort((a, b) => a.bucketSeqId - b.bucketSeqId);
            const minSeqId = sorted[0].bucketSeqId;
            const maxSeqId = sorted[sorted.length - 1].bucketSeqId;

            sorted.forEach((record) => {
                const processedRecord: InboundBridgeTableRecord = {
                    ...record,
                    bucketType: record.bucketSeqId === minSeqId ? 'plan' : record.bucketSeqId === maxSeqId ? 'actuals' : 'none',
                };
                result.push(processedRecord);
            });
        });

        return result;
    }

    return records.map((record) => ({
        ...record,
        bucketType: 'none',
    }));
}

function formatCellText(bucketType: 'plan' | 'actuals' | 'none', text?: string | number) {
    return bucketType === 'none' ? text ?? '-' : <strong>{text ?? '-'}</strong>;
}

export type InboundBridgeTableRecord = {
    date: string;
    node: string;
    granularity: Record<string, string>;
    bucketSeqId: number;
    bucketName: string;
    bucketType: 'plan' | 'actuals' | 'none';
    bucketValue: number;
    bucketError: number;
    lastBucketValue: number;
    bucketWape: string;
};
